import { debounceTime, distinctUntilChanged, fromEvent, map, of, switchMap, tap } from 'rxjs'
import { generatePath, useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { ColumnsType } from 'antd/lib/table'
import { useRecoilValue } from 'recoil'
import { Badge } from 'antd'
import dayjs from 'dayjs'

import {
  ActionBlock,
  BodySpan,
  Box,
  ButtonIcon,
} from '@/components/feature/WellBeingPage/WellBeingPartnerTable/styles'
import {
  ActiveHealthPartnerInterface,
  ActiveHealthPartnerInputInterface,
} from '@/gql/activeHealthPartner/backofficeGetActiveHealthPartnerPagination/interfaces'
import ConfirmModalHook from '@/components/common/ConfirmModal/ConfirmModal.hook'
import { fetchPartnerCanDelete } from '@/services/wellBeing/usePartnerCanDelete'
import { BACKOFFICE_USER_PERMISSION } from '@/constant/BACKOFFICE_PERMISSION'
import { PartnerColumnContainer, PartnerImage } from './styles'
import { WellBeingModeEnum } from '@/constant/WELL_BEING'
import { validatePerm } from '@/libs/validatePermission'
import { SorterResult } from 'antd/lib/table/interface'
import { SORTER, SorterEnum } from '@/constant/USER'
import { SortByEnum } from '@/constant/SORT_BY'
import { activeHealthPartnerGql } from '@/gql'
import { IMAGE_URL } from '@/constant/IMAGE'
import userAtom from '@/recoil/user/atom'
import message from '@/libs/message'
import CONSTANT from '@/constant'

const WellBeingPartnerTableHook = () => {
  const user = useRecoilValue(userAtom)
  const navigate = useNavigate()

  const isAddPartner = validatePerm([BACKOFFICE_USER_PERMISSION.WBE010102], user.permissions)
  const isViewPartner = validatePerm([BACKOFFICE_USER_PERMISSION.WBE010103], user.permissions)
  const isEditPartner = validatePerm([BACKOFFICE_USER_PERMISSION.WBE010104], user.permissions)
  const isRemovePartner = validatePerm([BACKOFFICE_USER_PERMISSION.WBE010106], user.permissions)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const searchElement = document.getElementById('well-being-search')
  const [isTyping, setIsTyping] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')
  const [sort, setSort] = useState<[string, SortByEnum | '']>(['createdAt', SortByEnum.DESC])
  const [pagination, setPagination] = useState<{
    page: number
    limit: number
    totalItems?: number
  }>({
    page: 1,
    limit: 10,
    totalItems: 0,
  })
  const { setConfirmModal } = ConfirmModalHook()

  // Fetch partner pagination
  const [
    getActiveHealthPartnerPagination,
    {
      data: activeHealthPartnerData,
      loading: activeHealthPartnerLoading,
      refetch: refetchPagination,
    },
  ] = useLazyQuery(activeHealthPartnerGql.query.backofficeGetActiveHealthPartnerPagination, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data.backofficeGetActiveHealthPartnerPagination.meta) {
        setPagination({
          page: data.backofficeGetActiveHealthPartnerPagination.meta.currentPage,
          limit: data.backofficeGetActiveHealthPartnerPagination.meta.itemsPerPage,
          totalItems: data.backofficeGetActiveHealthPartnerPagination.meta.totalItems,
        })
      }
    },
  })

  // Use GraphQL mutation for removing partner
  const [mutationRemovePartner] = useMutation(
    activeHealthPartnerGql.mutation.backofficeRemoveActiveHealthPartner,
    {
      fetchPolicy: 'no-cache',
    },
  )

  // Handle remove partner
  const handleRemovePartner = async (partnerId: string): Promise<void> => {
    if (!partnerId) return

    try {
      await mutationRemovePartner({ variables: { activeHealthPartnerId: partnerId } })
      await refetchPagination()
      message.success({ content: 'ลบพาร์ทเนอร์สำเร็จ', duration: 10 })
    } catch (error: any) {
      message.error({ content: error?.response?.data?.message || 'กรุณาตรวจสอบข้อมูลให้ถูกต้อง' })
    }
  }

  // Modal confirm delete
  const showDeleteConfirmationModal = (data: ActiveHealthPartnerInterface) => {
    const partnerName = `“${data?.displayNameTh} (${data?.displayNameEn})”`

    setConfirmModal({
      isVisible: true,
      title: 'คุณแน่ใจที่จะลบพาร์ทเนอร์หรือไม่',
      bodyText: `เมื่อลบพาร์ทเนอร์ ${partnerName} พาร์ทเนอร์และสาขาภายใต้พาร์ทเนอร์นี้จะ ถูกลบไปทั้งหมด`,
      okText: 'ลบ',
      cancelText: 'ยกเลิก',
      dataTestId: 'remove-partner-information-modal',
      okButtonProps: { type: 'primary', danger: true },
      onOkFunction: () => {
        handleRemovePartner(data.id)
        setConfirmModal({
          isVisible: false,
          onOkFunction: Function,
        })
      },
    })
  }

  // Modal partner cannot delete
  const showCannotDeleteModal = () => {
    setConfirmModal({
      isVisible: true,
      title: 'ไม่สามารถลบพาร์ทเนอร์ได้',
      bodyText: 'เนื่องจากมีสาขาที่มีการใช้งานสแกนรับ Well Point แล้ว',
      okText: 'รับทราบ',
      cancelText: undefined,
      cancelButtonProps: { style: { display: 'none' } },
      dataTestId: 'remove-partner-information-modal',
      onOkFunction: () => {
        setConfirmModal({
          isVisible: false,
          onOkFunction: Function,
        })
      },
    })
  }

  // Button remove partner
  const onClickRemove = async (data: ActiveHealthPartnerInterface): Promise<void> => {
    if (!data.id) return

    const result = await fetchPartnerCanDelete(data.id)

    result.canDelete ? showDeleteConfirmationModal(data) : showCannotDeleteModal()
  }

  const columns: ColumnsType<ActiveHealthPartnerInterface | {}> = [
    {
      title: 'ลำดับ',
      align: 'center',
      render: (_, __, index) => (pagination.page - 1) * pagination.limit + index + 1,
    },
    {
      title: 'ชื่อพาร์ทเนอร์',
      key: 'displayNameEn',
      sorter: true,
      sortDirections: [SorterEnum.DESC, SorterEnum.ASC],
      defaultSortOrder: null,
      render: (_, record) => {
        const data = record as ActiveHealthPartnerInterface

        return (
          <PartnerColumnContainer>
            <BodySpan>
              <PartnerImage src={data.logoImageUrl} />
            </BodySpan>
            <BodySpan>
              <Box>{data.displayNameEn}</Box>
              <Box style={{ color: '#666666' }}>{data.displayNameTh}</Box>
            </BodySpan>
          </PartnerColumnContainer>
        )
      },
    },
    {
      title: 'สถานะ',
      dataIndex: 'isActive',
      key: 'isActive',
      sorter: true,
      sortDirections: [SorterEnum.DESC, SorterEnum.ASC],
      defaultSortOrder: null,
      render: (isActive: boolean) => {
        return (
          <>
            <Badge status={isActive ? 'success' : 'error'} />{' '}
            {isActive ? 'เปิดการใช้งาน' : 'ปิดการใช้งาน'}
          </>
        )
      },
    },
    {
      title: 'จำนวนสาขา',
      render: (_, record) => {
        const data = record as ActiveHealthPartnerInterface
        return data?.activeHealthPartnerBranch?.length || '-'
      },
    },
    {
      title: 'หมวดหมู่',
      dataIndex: ['activeHealthPartnerCategory', 'displayNameEn'],
    },
    {
      title: 'สร้างโดย',
      dataIndex: 'createdByText',
    },
    {
      title: 'แก้ไขล่าสุด',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      sortDirections: [SorterEnum.DESC, SorterEnum.ASC],
      defaultSortOrder: null,
      render: (updatedAt: Date) => {
        return dayjs(updatedAt).format('DD-MM-YYYY')
      },
    },
    {
      fixed: 'right',
      render: (_, record) => {
        const data = record as ActiveHealthPartnerInterface

        return (
          <ActionBlock key={data?.id}>
            {isViewPartner && (
              <ButtonIcon
                src={IMAGE_URL.eyeIcon}
                onClick={() => onSelectPartner(data?.id, WellBeingModeEnum.VIEW)}
              />
            )}
            {isEditPartner && (
              <ButtonIcon
                src={IMAGE_URL.editGreyIcon}
                onClick={() => onSelectPartner(data?.id, WellBeingModeEnum.EDIT)}
              />
            )}
            {isRemovePartner && (
              <ButtonIcon src={IMAGE_URL.deleteGreyIcon} onClick={() => onClickRemove(data)} />
            )}
          </ActionBlock>
        )
      },
    },
  ]

  const onSelectPartner = (id: string, action: WellBeingModeEnum) => {
    navigate(
      generatePath(CONSTANT.ROUTES.WELL_BEING_PARTNER_ID_PAGE, {
        partnerId: id,
      }),
      {
        state: {
          referrer: CONSTANT.ROUTES.WELL_BEING_PAGE,
          action,
        },
      },
    )
  }

  const getActiveHealthPartner = useCallback(
    (input?: ActiveHealthPartnerInputInterface) => {
      getActiveHealthPartnerPagination({
        variables: {
          input: {
            search: input?.search || '',
            searchBy: ['displayNameTh', 'displayNameEn'],
            page: pagination.page || 1,
            limit: pagination.limit || 10,
            sortBy: input?.sortBy,
          },
        },
      })
    },
    [getActiveHealthPartnerPagination, pagination.limit, pagination.page],
  )

  const onTablePaginate = (page: number, pageSize: number) => {
    setPagination({
      page,
      limit: pageSize,
    })
  }

  const onTableChange = (sorter: SorterResult<{}> | Array<SorterResult<{}>>) => {
    if (sorter) {
      const sort = sorter as SorterResult<{}>
      const sortBy = sort.columnKey as string
      const sortOrder = sort.order as SorterEnum

      setSort([sortBy, SORTER[sortOrder] || ''])
    }
  }

  const onSearchInputChange = (isTyping: boolean) => setIsTyping(isTyping)

  const handleSetSearch = (input: string) =>
    of(setSearch(input)).pipe(
      tap(() => {
        setPagination({
          page: 1,
          limit: 10,
          totalItems: pagination.totalItems,
        })
      }),
    )

  const handleAddPartner = () => {
    setIsModalOpen(true)
  }

  const handleModalOk = () => {
    refetchPagination()
    setIsModalOpen(false)
  }

  const handleModalCancel = () => {
    setIsModalOpen(false)
  }

  useEffect(() => {
    getActiveHealthPartner({
      search,
      limit: pagination.limit,
      page: pagination.page,
      sortBy: sort[0] ? [sort] : undefined,
    })
  }, [search, pagination.page, pagination.limit, sort])

  useEffect(() => {
    if (isTyping && searchElement) {
      fromEvent(searchElement, 'keyup')
        .pipe(
          debounceTime(1000),
          map((e: Event) => {
            const target = e.target as HTMLInputElement

            return target.value || ''
          }),
          distinctUntilChanged(),
          switchMap(handleSetSearch),
          tap(() => setIsTyping(false)),
        )
        .subscribe()
    }
  }, [isTyping, searchElement])

  return {
    activeHealthPartnerLoading,
    activeHealthPartnerData,
    isAddPartner,
    isModalOpen,
    pagination,
    columns,
    onTableChange,
    onTablePaginate,
    onSearchInputChange,
    handleAddPartner,
    handleModalOk,
    handleModalCancel,
  }
}

export default WellBeingPartnerTableHook
